export type FileFormat = 'json' | 'csv';

/* ── Download ── */

/** Download an object as a JSON file */
export const downloadJson = (data: unknown, filename: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  downloadBlob(blob, filename.endsWith('.json') ? filename : `${filename}.json`);
};

/** Download an array of objects as a CSV file */
export const downloadCsv = (data: Record<string, unknown>[], filename: string) => {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const toString = (v: unknown): string => {
    if (v == null) return '';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v as string | number | boolean);
  };
  const escape = (v: unknown): string => {
    const s = toString(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [keys.join(','), ...data.map(row => keys.map(k => escape(row[k])).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, filename.endsWith('.csv') ? filename : `${filename}.csv`);
};

/** Download data in the specified format */
export const downloadFile = (data: Record<string, unknown>[], filename: string, format: FileFormat) => {
  if (format === 'csv') downloadCsv(data, filename);
  else downloadJson(data, filename);
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/* ── Read / Import ── */

/** Open a file picker and read a file. Supports JSON and CSV. */
export const readFile = <T = unknown>(accept: string = '.json,.csv'): Promise<{ data: T | null; format: FileFormat }> =>
  new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) { resolve({ data: null, format: 'json' }); return; }
      const isCsv = file.name.toLowerCase().endsWith('.csv');
      const reader = new FileReader();
      reader.onload = () => {
        try {
          if (isCsv) {
            resolve({ data: parseCsv(reader.result as string) as unknown as T, format: 'csv' });
          } else {
            resolve({ data: JSON.parse(reader.result as string), format: 'json' });
          }
        } catch { resolve({ data: null, format: isCsv ? 'csv' : 'json' }); }
      };
      reader.onerror = () => resolve({ data: null, format: 'json' });
      reader.readAsText(file);
    };
    input.click();
  });

/** Kept for backwards compatibility */
export const readJsonFile = <T = unknown>(): Promise<T | null> =>
  readFile<T>('.json,application/json').then(r => r.data);

/* ── CSV Parser ── */

const parseCsv = (text: string): Record<string, string>[] => {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]);
  return lines.slice(1).map(line => {
    const values = parseCsvLine(line);
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = values[i] ?? ''; });
    return obj;
  });
};

const parseCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { current += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { result.push(current); current = ''; }
      else { current += ch; }
    }
  }
  result.push(current);
  return result;
};
